import { AlertCircle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import {
  ToastContext,
  type ToastContextValue,
  type ToastVariant,
} from "./toastContext";

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

const styles: Record<ToastVariant, string> = {
  success: "border-success/30 bg-card text-success",
  error: "border-danger/30 bg-card text-danger",
  warning: "border-warning/30 bg-card text-warning",
  info: "border-brand-primary/30 bg-card text-brand-primary",
};

const icons = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const remove = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);
  const show = useCallback(
    (message: string, variant: ToastVariant = "info") => {
      const id = ++nextId.current;
      setItems((current) => [...current.slice(-3), { id, message, variant }]);
      window.setTimeout(() => remove(id), 4000);
    },
    [remove],
  );
  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (message) => show(message, "success"),
      error: (message) => show(message, "error"),
      warning: (message) => show(message, "warning"),
      info: (message) => show(message, "info"),
    }),
    [show],
  );
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDismiss={remove} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  const { dir, t } = useTranslation();
  return (
    <div
      dir={dir}
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-4 top-4 z-200 flex flex-col items-end gap-3 sm:inset-x-auto sm:inset-e-6 sm:w-[24rem]"
    >
      {items.map((item) => {
        const Icon = icons[item.variant];
        return (
          <div
            key={item.id}
            role={item.variant === "error" ? "alert" : "status"}
            className={`pointer-events-auto flex w-full items-start gap-3 rounded-2xl border p-4 shadow-xl ${styles[item.variant]}`}
            style={{ animation: "toastMessageIn 180ms ease-out" }}
          >
            <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1 text-sm font-medium leading-6 text-card-foreground">
              {item.message}
            </p>
            <button
              type="button"
              onClick={() => onDismiss(item.id)}
              aria-label={t("common.close")}
              className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes toastMessageIn {
          from { opacity: 0; transform: translateY(-8px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
